import { useTranslation } from "@/i18n/useTranslation";

const LANES = [
  { x: 72, y: 86, width: 184 },
  { x: 294, y: 86, width: 184 },
  { x: 516, y: 86, width: 184 },
  { x: 738, y: 86, width: 184 },
];

const CHECKS = [
  { x: 112, y: 250 },
  { x: 304, y: 306 },
  { x: 498, y: 250 },
  { x: 690, y: 306 },
  { x: 824, y: 250 },
];

type HeroBackgroundProps = {
  className?: string;
  quiet?: boolean;
  tone?: "auto" | "dark" | "light";
};

const svgOpacityByTone = {
  auto: "opacity-[0.16] md:opacity-[0.22] xl:opacity-[0.28] dark:opacity-[0.2] dark:md:opacity-[0.28] dark:xl:opacity-[0.34]",
  dark: "opacity-[0.2] md:opacity-[0.28] xl:opacity-[0.34]",
  light: "opacity-[0.12] md:opacity-[0.16] xl:opacity-[0.2]",
} as const;

const quietSvgOpacityByTone = {
  auto: "opacity-[0.1] md:opacity-[0.14] xl:opacity-[0.18] dark:opacity-[0.13] dark:md:opacity-[0.2] dark:xl:opacity-[0.24]",
  dark: "opacity-[0.13] md:opacity-[0.2] xl:opacity-[0.24]",
  light: "opacity-[0.08] md:opacity-[0.11] xl:opacity-[0.14]",
} as const;

const gridOpacityByTone = {
  auto: { x: "0.026", y: "0.018" },
  dark: { x: "0.03", y: "0.022" },
  light: { x: "0.024", y: "0.016" },
} as const;

const HeroBackground = ({ className = "", quiet = false, tone = "auto" }: HeroBackgroundProps) => {
  const { locale } = useTranslation();
  const isFr = locale === "fr";
  const svgOpacity = quiet ? quietSvgOpacityByTone[tone] : svgOpacityByTone[tone];
  const gridOpacity = gridOpacityByTone[tone];
  const laneLabels = isFr
    ? ["DONNÉES", "ÉVALUER", "VALIDER", "EXPLOITER"]
    : ["DATA", "EVALUATE", "REVIEW", "OPERATE"];
  const checkLabels = isFr
    ? ["qualité", "règles", "humain", "surveiller", "rendement"]
    : ["quality", "policy", "human", "observe", "roi"];

  return (
  <div aria-hidden="true" className={`absolute inset-0 pointer-events-none overflow-hidden ${className}`}>
    <div
      className="absolute inset-0"
      style={{
        backgroundImage: `
          linear-gradient(90deg, hsl(var(--sd-fg) / ${gridOpacity.x}) 1px, transparent 1px),
          linear-gradient(0deg, hsl(var(--sd-fg) / ${gridOpacity.y}) 1px, transparent 1px)
        `,
        backgroundSize: "96px 96px",
        maskImage: "linear-gradient(90deg, transparent 0%, black 24%, black 82%, transparent 100%)",
        WebkitMaskImage: "linear-gradient(90deg, transparent 0%, black 24%, black 82%, transparent 100%)",
      }}
    />
    <svg
      viewBox="0 0 1000 520"
      className={`absolute inset-0 h-full w-full translate-x-[22%] sm:translate-x-[12%] lg:translate-x-[8%] ${svgOpacity}`}
      preserveAspectRatio="xMidYMid slice"
      fill="none"
      aria-hidden="true"
    >
      <rect x="54" y="58" width="892" height="376" stroke="hsl(var(--sd-fg))" strokeOpacity="0.08" />
      <path d="M54 146H946" stroke="hsl(var(--sd-fg))" strokeOpacity="0.055" />
      <path d="M54 234H946" stroke="hsl(var(--sd-fg))" strokeOpacity="0.055" />
      <path d="M54 322H946" stroke="hsl(var(--sd-fg))" strokeOpacity="0.055" />

      {LANES.map((lane, index) => (
        <g key={laneLabels[index]}>
          <rect
            x={lane.x}
            y={lane.y}
            width={lane.width}
            height="68"
            fill="hsl(var(--sd-fg))"
            fillOpacity={index === 3 ? "0.07" : "0.035"}
            stroke="hsl(var(--sd-fg))"
            strokeOpacity={index === 3 ? "0.16" : "0.09"}
          />
          <text
            x={lane.x + 18}
            y={lane.y + 30}
            fill="hsl(var(--sd-fg))"
            fillOpacity={index === 3 ? "0.68" : "0.42"}
            fontFamily="JetBrains Mono, monospace"
            fontSize="10"
            letterSpacing="0.12em"
          >
            {String(index + 1).padStart(2, "0")} / {laneLabels[index]}
          </text>
          <path
            d={`M${lane.x + 18} ${lane.y + 48}H${lane.x + lane.width - 18}`}
            stroke={index === 3 ? "hsl(var(--primary))" : "hsl(var(--sd-fg))"}
            strokeOpacity={index === 3 ? "0.42" : "0.12"}
          />
        </g>
      ))}

      <path
        d="M256 120H294M478 120H516M700 120H738"
        stroke="hsl(var(--primary))"
        strokeOpacity="0.36"
        strokeWidth="1.2"
      />
      <path
        d="M498 184L554 232L498 280L442 232Z"
        fill="hsl(var(--primary))"
        fillOpacity="0.055"
        stroke="hsl(var(--primary))"
        strokeOpacity="0.28"
      />
      <text
        x="498"
        y="236"
        textAnchor="middle"
        fill="hsl(var(--sd-fg))"
        fillOpacity="0.48"
        fontFamily="JetBrains Mono, monospace"
        fontSize="9"
        letterSpacing="0.12em"
      >
        {isFr ? "POINT DE DÉCISION" : "DECISION GATE"}
      </text>

      <path d="M256 306H442" stroke="hsl(var(--sd-fg))" strokeOpacity="0.12" strokeDasharray="4 7" />
      <path d="M554 306H738" stroke="hsl(var(--sd-fg))" strokeOpacity="0.12" strokeDasharray="4 7" />
      <path d="M498 280V360" stroke="hsl(var(--primary))" strokeOpacity="0.24" />
      <rect x="368" y="360" width="260" height="42" fill="hsl(var(--sd-fg))" fillOpacity="0.035" stroke="hsl(var(--sd-fg))" strokeOpacity="0.09" />
      <text
        x="498"
        y="386"
        textAnchor="middle"
        fill="hsl(var(--sd-fg))"
        fillOpacity="0.46"
        fontFamily="JetBrains Mono, monospace"
        fontSize="9"
        letterSpacing="0.12em"
      >
        {isFr ? "OBSERVABILITÉ / TRANSFERT / RENDEMENT" : "OBSERVABILITY / ESCALATION / ROI"}
      </text>

      {CHECKS.map((check, index) => (
        <g key={checkLabels[index]}>
          <circle cx={check.x} cy={check.y} r="4.5" fill="hsl(var(--primary))" fillOpacity={index === 2 ? "0.52" : "0.28"} />
          <circle cx={check.x} cy={check.y} r="14" stroke="hsl(var(--sd-fg))" strokeOpacity="0.055" />
          <text
            x={check.x + 22}
            y={check.y + 4}
            fill="hsl(var(--sd-fg))"
            fillOpacity="0.28"
            fontFamily="JetBrains Mono, monospace"
            fontSize="8"
            letterSpacing="0.1em"
          >
            {checkLabels[index].toUpperCase()}
          </text>
        </g>
      ))}
    </svg>
  </div>
  );
};

export default HeroBackground;
